import { useEffect, useMemo, useState } from "react";
import Button from "../ui/Button.jsx";
import { useToast } from "../../hooks/useToast.js";
import { mapAdminError } from "../../utils/adminApiErrors.js";
import ReassignmentTargetSelect from "./ReassignmentTargetSelect.jsx";
import { formatName } from "../../utils/adminUserFormat.js";

const inlineAlertStyle = { margin: "0 0 12px", color: "var(--critical)", fontSize: "13px" };

export default function DeactivateUserPanel({ user, actorId, onCancel, onDone }) {
  const { show } = useToast();
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState("");
  const [handling, setHandling] = useState("delete");
  const [reassignToUserId, setReassignToUserId] = useState("");
  const [confirmDeleteText, setConfirmDeleteText] = useState("");
  const [targets, setTargets] = useState([]);
  const [loadingTargets, setLoadingTargets] = useState(false);

  const projects = Array.isArray(user?.projects) ? user.projects : [];
  const projectCount = projects.length;

  useEffect(() => {
    let cancel = false;
    async function loadTargets() {
      setLoadingTargets(true);
      try {
        const res = await fetch("/api/admin/users?include_deleted=true", { credentials: "include" });
        if (!res.ok) return;
        const body = await res.json().catch(() => ({}));
        const users = Array.isArray(body?.users) ? body.users : [];
        const options = users
          .filter((u) => String(u.status || "").toLowerCase() === "active")
          .filter((u) => u.id !== user?.id)
          .filter((u) => u.id !== actorId)
          .map((u) => ({
            id: u.id,
            label: `${formatName(u.name)} (${u.email || "unknown"})`,
          }));
        if (!cancel) setTargets(options);
      } finally {
        if (!cancel) setLoadingTargets(false);
      }
    }
    loadTargets();
    return () => {
      cancel = true;
    };
  }, [user?.id, actorId]);

  const canReassign = targets.length > 0;
  const needsDeleteConfirm = projectCount > 0 && handling === "delete";
  const deleteConfirmed = confirmDeleteText === "DELETE";

  useEffect(() => {
    if (projectCount === 0) {
      setHandling("delete");
      return;
    }
    if (!canReassign && handling === "reassign") {
      setHandling("delete");
    }
  }, [projectCount, canReassign, handling]);

  const submitDisabled = useMemo(() => {
    if (loading) return true;
    if (projectCount === 0) return false;
    if (handling === "reassign") return !reassignToUserId;
    if (needsDeleteConfirm) return !deleteConfirmed;
    return false;
  }, [loading, projectCount, handling, reassignToUserId, needsDeleteConfirm, deleteConfirmed]);

  const deleteConfirmError =
    needsDeleteConfirm && confirmDeleteText.length > 0 && !deleteConfirmed
      ? "Type DELETE exactly to confirm."
      : null;

  async function submitDeactivate() {
    setLoading(true);
    try {
      const payload =
        handling === "reassign"
          ? { project_handling: "reassign", reassign_to_user_id: reassignToUserId, reason: reason.trim() || undefined }
          : { project_handling: "delete", reason: reason.trim() || undefined };

      const res = await fetch(`/api/admin/users/${encodeURIComponent(user.id)}/deactivate`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (body?.error === "already_deactivated") {
          show("User is already deactivated", "error");
        } else if (body?.error === "invalid_reassign_target") {
          show("Selected user is no longer available for reassignment. Try another.", "error");
        } else {
          show(mapAdminError(body?.error), "error");
        }
        return;
      }
      const targetLabel = targets.find((t) => t.id === reassignToUserId)?.label || "selected user";
      const suffix =
        handling === "reassign"
          ? `${projectCount} project(s) reassigned to ${targetLabel}.`
          : `${projectCount} project(s) deleted.`;
      show(`User deactivated. ${suffix}`, "success");
      onDone?.();
    } catch {
      show("Failed to deactivate user. Try again.", "error");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <h3 style={{ margin: "0 0 10px", fontSize: "18px", fontWeight: 600 }}>
        Deactivate {formatName(user?.name)}?
      </h3>
      <p className="text-secondary" style={{ margin: "0 0 14px", fontSize: "14px", lineHeight: 1.5 }}>
        Deactivating prevents this user from signing in. You must decide what to do with their projects.
      </p>

      {projectCount === 0 ? (
        <p className="text-secondary" style={{ margin: "0 0 14px", fontSize: "14px" }}>
          This user has no projects.
        </p>
      ) : (
        <>
          <div style={{ marginBottom: "14px" }}>
            <div style={{ fontSize: "12px", fontWeight: 600, marginBottom: "6px" }}>Projects ({projectCount})</div>
            <ul style={{ margin: 0, paddingLeft: "18px", fontSize: "13px" }}>
              {projects.map((p) => (
                <li key={p.project_id}>{p.project_name || p.project_id}</li>
              ))}
            </ul>
          </div>

          <div style={{ marginBottom: "14px" }}>
            <label style={{ display: "block", fontSize: "12px", fontWeight: 600, marginBottom: "6px" }}>
              Reason for deactivation (optional)
            </label>
            <textarea
              value={reason}
              maxLength={512}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason for deactivation (optional)"
              style={{
                width: "100%",
                minHeight: "82px",
                padding: "10px 12px",
                fontSize: "14px",
                borderRadius: "4px",
                border: "1px solid var(--border-bright)",
                background: "var(--bg-elevated)",
                color: "var(--text-primary)",
                fontFamily: "inherit",
                boxSizing: "border-box",
                resize: "vertical",
              }}
            />
          </div>

          <div style={{ marginBottom: "10px" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px", opacity: canReassign ? 1 : 0.6 }}>
              <input
                type="radio"
                checked={handling === "reassign"}
                onChange={() => setHandling("reassign")}
                disabled={!canReassign || loadingTargets}
              />
              Reassign all projects to another user
            </label>
            {canReassign ? (
              <ReassignmentTargetSelect
                options={targets}
                value={reassignToUserId}
                onChange={setReassignToUserId}
                disabled={handling !== "reassign" || loadingTargets}
                placeholder={loadingTargets ? "Loading users..." : "Select reassignment target"}
              />
            ) : (
              <p className="text-secondary" style={{ margin: "0 0 8px", fontSize: "12px" }}>
                No active users available — projects must be deleted instead.
              </p>
            )}
          </div>

          <div style={{ marginBottom: "14px" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
              <input type="radio" checked={handling === "delete"} onChange={() => setHandling("delete")} />
              Delete all projects
            </label>
            {handling === "delete" ? (
              <>
                <label style={{ display: "block", fontSize: "12px", fontWeight: 600, marginBottom: "6px" }}>
                  Type DELETE to confirm
                </label>
                <input
                  value={confirmDeleteText}
                  onChange={(e) => setConfirmDeleteText(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    fontSize: "14px",
                    borderRadius: "4px",
                    border: "1px solid var(--border-bright)",
                    background: "var(--bg-elevated)",
                    color: "var(--text-primary)",
                    fontFamily: "inherit",
                    boxSizing: "border-box",
                  }}
                />
                {deleteConfirmError ? (
                  <p role="alert" style={{ ...inlineAlertStyle, marginTop: "8px" }}>
                    {deleteConfirmError}
                  </p>
                ) : null}
              </>
            ) : null}
          </div>
        </>
      )}

      <div style={{ display: "flex", gap: "10px" }}>
        <Button variant="secondary" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button variant="danger" onClick={submitDeactivate} loading={loading} disabled={submitDisabled}>
          Deactivate
        </Button>
      </div>
    </div>
  );
}
